import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wrench, Calendar as CalendarIcon, User, Building2, Plus, Trash2, ExternalLink
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SearchSelect from '../components/SearchSelect';
import { maintenanceAPI, propertyAPI } from '../lib/api';
import { formatDate, getGoogleMapsUrl } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Maintenance = () => {
  const [maintenances, setMaintenances] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    property_id: '',
    maintenance_date: '',
    maintenance_time: '09:00',
    maintenance_type: '',
    technician: '',
    notes: ''
  });
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [maintRes, propsRes] = await Promise.all([
        maintenanceAPI.getAll(),
        propertyAPI.getAll()
      ]);
      setMaintenances(maintRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      toast.error('Kon onderhoud niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Combine date and time
      const maintenanceDateTime = formData.maintenance_time 
        ? `${formData.maintenance_date}T${formData.maintenance_time}`
        : formData.maintenance_date;
      
      await maintenanceAPI.create({
        property_id: formData.property_id,
        maintenance_date: maintenanceDateTime,
        maintenance_type: formData.maintenance_type,
        technician: formData.technician,
        notes: formData.notes
      });
      toast.success('Onderhoud ingepland');
      setDialogOpen(false);
      setFormData({ property_id: '', maintenance_date: '', maintenance_time: '09:00', maintenance_type: '', technician: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Kon onderhoud niet toevoegen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit onderhoud wilt verwijderen?')) return;
    try {
      await maintenanceAPI.delete(id);
      toast.success('Onderhoud verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon niet verwijderen');
    }
  };

  const getPropertyAddress = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? `${prop.street} ${prop.number}, ${prop.city}` : '';
  };

  const getProperty = (propertyId) => {
    return properties.find(p => p.id === propertyId);
  };

  // Prepare property options for search select
  const propertyOptions = properties.map(p => ({
    value: p.id,
    label: `${p.street} ${p.number}, ${p.postal_code} ${p.city}`
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="maintenance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Onderhoud</h1>
          <p className="text-muted-foreground mt-1">Beheer onderhoudsactiviteiten</p>
        </div>
        {hasRole(['admin', 'technician']) && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-maintenance-btn">
                <Plus className="w-4 h-4 mr-2" />
                Onderhoud Inplannen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Onderhoud Inplannen</DialogTitle>
                <DialogDescription>Plan een nieuwe onderhoudsbeurt in</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Adres *</Label>
                  <SearchSelect
                    options={propertyOptions}
                    value={formData.property_id}
                    onChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
                    placeholder="Zoek adres..."
                    displayKey="label"
                    valueKey="value"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Datum *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.maintenance_date ? format(parse(formData.maintenance_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.maintenance_date ? parse(formData.maintenance_date, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => date && setFormData(prev => ({ ...prev, maintenance_date: format(date, 'yyyy-MM-dd') }))}
                          locale={nl}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Tijd *</Label>
                    <Input
                      type="time"
                      value={formData.maintenance_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, maintenance_time: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type onderhoud *</Label>
                    <Input
                      value={formData.maintenance_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, maintenance_type: e.target.value }))}
                      placeholder="bv. Jaarlijkse keuring, Omvormer reparatie"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Technicus *</Label>
                    <Input
                      value={formData.technician}
                      onChange={(e) => setFormData(prev => ({ ...prev, technician: e.target.value }))}
                      placeholder="Naam technicus"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notities</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    placeholder="Extra opmerkingen..."
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
                  <Button type="submit" disabled={saving || !formData.property_id}>
                    {saving ? 'Opslaan...' : 'Inplannen'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Maintenance List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="h-20 animate-pulse bg-muted" />
          ))}
        </div>
      ) : maintenances.length === 0 ? (
        <Card className="p-12 text-center">
          <Wrench className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-lg font-medium text-muted-foreground">Nog geen onderhoud gepland</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {maintenances.map((m) => (
            <Card key={m.id} className="border-border/50" data-testid={`maintenance-card-${m.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-md bg-amber-500/20 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="font-medium">{m.maintenance_type}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {formatDate(m.maintenance_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {m.technician}
                      </span>
                      <span 
                        className="flex items-center gap-1 text-primary cursor-pointer hover:underline"
                        onClick={() => navigate(`/properties/${m.property_id}`)}
                      >
                        <Building2 className="w-3 h-3" />
                        {getPropertyAddress(m.property_id)}
                      </span>
                      {getProperty(m.property_id) && (
                        <a
                          href={getGoogleMapsUrl(getProperty(m.property_id))}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-muted-foreground hover:text-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Maps
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {m.notes && <p className="text-sm text-muted-foreground max-w-xs truncate">{m.notes}</p>}
                  {hasRole('admin') && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(m.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Maintenance;
